import z from 'zod';
import {
  ApiClient,
  ApiClientContext,
  ApiClientOptions,
  RouteOptions,
  defaultAxiosConfig,
} from '@qualifio/api-client';
import { AxiosInstance } from 'axios';
import { OrganisationTenantsDto, Tenant } from './types/tenant';

export enum Module {
  LOYALTY = 'LOYALTY',
  ENGAGE = 'ENGAGE',
}

export type ExternalTenantLinksByModule = Record<
  Module,
  {
    [externalTenantId: string]: string[] | 'all';
  }
>;

export const $GetTenantQueryParams = z.object({
  idType: z.enum(['id', 'publicTenantId', 'externalTenantId']),
  module: z.enum([Module.LOYALTY, Module.ENGAGE]), // todo migrate Module to zod require migrating other repository
});
export type GetTenantQueryParams = z.infer<typeof $GetTenantQueryParams>;

export interface OrganisationApiClientOptions extends ApiClientOptions {
  token?: string;
}

export class OrganisationApiClient extends ApiClient {
  static serviceName = 'organisations-api';
  static scopeName = 'internal-srv-organisations';

  constructor(
    http?: AxiosInstance,
    context?: ApiClientContext,
    options?: OrganisationApiClientOptions,
  ) {
    super('/', http, context, options);
  }

  async getOrganisationsTenants(options?: RouteOptions): Promise<{
    organisations: OrganisationTenantsDto[];
  }> {
    const { data } = await this.http.get('/organisations/tenants', {
      ...defaultAxiosConfig(options),
      params: { ...options?.params },
    });
    return data;
  }

  async getTenant(
    identifier: string,
    options: RouteOptions<GetTenantQueryParams> = {
      params: { idType: 'externalTenantId', module: Module.LOYALTY },
    },
  ): Promise<Tenant> {
    const { data } = await this.http.get(`/tenants/${identifier}`, defaultAxiosConfig(options));
    return data;
  }

  async findAuthorizedTenants(
    module: Module,
    externalTenantId: string, // accountId in loyalty module
    entityId?: string, // programId in loyalty module
    options?: RouteOptions,
  ): Promise<ExternalTenantLinksByModule> {
    const { data } = await this.http.get(`/module/${module}/external-tenant/${externalTenantId}`, {
      ...defaultAxiosConfig(options),
      params: { ...options?.params, entityId },
    });
    return data;
  }
}
